/**
 * ScanFilters — primary toolbar + active-filter chip strip.
 *
 * Reads/writes filter state via URL through useScanFilters().
 *
 * Legacy adapter: if the old `filters` + `onChange` props are passed (from
 * ScansComponent while Batch 6 migration is pending), they are silently
 * ignored and a one-time console.warn is emitted. The URL is always the
 * source of truth.
 */
import React, { useRef } from 'react';
import {
    Autocomplete,
    TextField,
    Chip,
    Button,
    Box,
    Typography,
    Select,
    MenuItem,
    styled,
    useTokens,
} from '@barracuda-internal/bds-core';
import { useScanFilters, VALID_CATEGORIES, VALID_ACTIONS, VALID_RANGES, VALID_PROVIDERS } from './hooks/useScanFilters';
import { useApiKeyList } from './hooks/useApiKeyList';
import { useDebouncedCallback } from './hooks/useDebouncedCallback';
import { CATEGORY_LABEL } from './findings/categoryToScanner';
import { PROVIDER_LABEL } from './findings/providerIcon';
import MoreFiltersPopover from './MoreFiltersPopover';

// ─── Display labels ────────────────────────────────────────────────────────────

const ACTION_LABEL = {
    allow:   'Allow',
    block:   'Block',
    partial: 'Partial',
};

const RANGE_LABEL = {
    '15m':   'Last 15 min',
    '1h':    'Last 1 hour',
    '24h':   'Last 24 hours',
    '7d':    'Last 7 days',
    '30d':   'Last 30 days',
    custom:  'Custom…',
};

// ─── Styled layout shells ──────────────────────────────────────────────────────
// styled() used for 3+ CSS properties; token values passed as props so that
// useTokens() is never called inside the styled callback.

const FilterRow = styled(Box, {
    shouldForwardProp: (prop) => !['gap'].includes(prop),
})(({ gap }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap,
    alignItems: 'flex-start',
}));

const ChipStrip = styled(Box, {
    shouldForwardProp: (prop) => !['gap', 'mt'].includes(prop),
})(({ gap, mt }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap,
    alignItems: 'center',
    marginTop: mt,
}));

// ─── Component ─────────────────────────────────────────────────────────────────

export default function ScanFilters({ filters: legacyFilters, onChange: legacyOnChange } = {}) {
    const tokens = useTokens();
    const { filters, setFilter, clearAll } = useScanFilters();
    const apiKeys = useApiKeyList();

    // Shared popper/menu background so dropdowns follow the current theme
    // instead of falling back to MUI's default white paper in dark mode.
    const themedPaperSx = {
        backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
        color: tokens.aliasesColorTextStaticEmphasis,
    };
    const menuProps = { PaperProps: { sx: themedPaperSx } };
    const autocompleteSlotProps = { paper: { sx: themedPaperSx } };

    // Legacy adapter — warn once if caller passes deprecated props
    const legacyWarnedRef = useRef(false);
    if ((legacyFilters !== undefined || legacyOnChange !== undefined) && !legacyWarnedRef.current) {
        legacyWarnedRef.current = true;
        // eslint-disable-next-line no-console
        console.warn(
            '[ScanFilters] The `filters` and `onChange` props are deprecated and will be removed ' +
            'in Batch 6. ScanFilters now reads/writes filter state via the URL (useScanFilters).',
        );
    }

    // Correlation ID: keep a local controlled value so typing is instant,
    // but only commit to URL after the debounce window.
    const [corrLocal, setCorrLocal] = React.useState(filters.correlationId);
    const commitCorr = useDebouncedCallback((v) => setFilter('correlationId', v), 500);

    // Sync back if URL changes externally (e.g. browser back)
    React.useEffect(() => {
        setCorrLocal(filters.correlationId);
    }, [filters.correlationId]);

    // Any filter that differs from default → show chip strip + "Clear all"
    const hasNonDefault =
        filters.range !== '24h' ||
        filters.apiKeyIds.length > 0 ||
        filters.actions.length > 0 ||
        filters.categories.length > 0 ||
        !!filters.correlationId ||
        !!filters.status ||
        !!filters.tenantId ||
        !!filters.userId ||
        !!filters.contentType ||
        filters.providers.length > 0;

    return (
        <Box>
            {/* ── Row 1: primary filter controls ── */}
            <FilterRow gap={tokens.aliasesSpaceComponentDefault}>
                <Select
                    value={filters.range}
                    onChange={(e) => {
                        const next = e.target.value;
                        if (next === 'custom' && !filters.from && !filters.to) {
                            // Seed defaults on first switch — last 24h
                            const now = new Date();
                            const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                            setFilter('range', 'custom');
                            setFilter('from', from.toISOString().slice(0, 16));
                            setFilter('to', now.toISOString().slice(0, 16));
                        } else {
                            setFilter('range', next);
                        }
                    }}
                    inputProps={{ 'aria-label': 'Time range' }}
                    MenuProps={menuProps}
                    sx={{ width: 180, flexShrink: 0 }}
                >
                    {VALID_RANGES.map((r) => (
                        <MenuItem key={r} value={r}>{RANGE_LABEL[r]}</MenuItem>
                    ))}
                </Select>

                {filters.range === 'custom' && (
                    <>
                        <TextField
                            type="datetime-local"
                            label="From"
                            value={filters.from ? filters.from.slice(0, 16) : ''}
                            onChange={(e) => setFilter('from', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 200, flexShrink: 0 }}
                            size="small"
                        />
                        <TextField
                            type="datetime-local"
                            label="To"
                            value={filters.to ? filters.to.slice(0, 16) : ''}
                            onChange={(e) => setFilter('to', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 200, flexShrink: 0 }}
                            size="small"
                        />
                    </>
                )}

                {/* Only show API key filter when there are multiple keys */}
                {apiKeys.length > 1 && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={apiKeys}
                        getOptionLabel={(o) => o.name || String(o.id)}
                        value={apiKeys.filter((k) => filters.apiKeyIds.includes(String(k.id)))}
                        onChange={(_, v) => setFilter('apiKeyIds', v.map((k) => String(k.id)))}
                        renderTags={() => null}
                        slotProps={autocompleteSlotProps}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    filters.apiKeyIds.length
                                        ? `API key (${filters.apiKeyIds.length})`
                                        : 'API key'
                                }
                            />
                        )}
                        sx={{ width: 180 }}
                    />
                )}

                <Autocomplete
                    multiple
                    size="small"
                    options={VALID_ACTIONS}
                    getOptionLabel={(o) => ACTION_LABEL[o] || o}
                    value={filters.actions}
                    onChange={(_, v) => setFilter('actions', v)}
                    renderTags={() => null}
                        slotProps={autocompleteSlotProps}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={filters.actions.length ? `Action (${filters.actions.length})` : 'Action'}
                        />
                    )}
                    sx={{ width: 160 }}
                />

                <Autocomplete
                    multiple
                    size="small"
                    options={VALID_CATEGORIES}
                    getOptionLabel={(o) => CATEGORY_LABEL[o] || o}
                    value={filters.categories}
                    onChange={(_, v) => setFilter('categories', v)}
                    renderTags={() => null}
                        slotProps={autocompleteSlotProps}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={
                                filters.categories.length
                                    ? `Findings (${filters.categories.length})`
                                    : 'Findings'
                            }
                        />
                    )}
                    sx={{ width: 200 }}
                />

                <Autocomplete
                    multiple
                    size="small"
                    options={VALID_PROVIDERS}
                    getOptionLabel={(o) => PROVIDER_LABEL[o] || o}
                    value={filters.providers}
                    onChange={(_, v) => setFilter('providers', v)}
                    renderTags={() => null}
                    slotProps={autocompleteSlotProps}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={
                                filters.providers.length
                                    ? `Provider (${filters.providers.length})`
                                    : 'Provider'
                            }
                        />
                    )}
                    sx={{ width: 180 }}
                />

                <TextField
                    label="Correlation ID"
                    value={corrLocal}
                    onChange={(e) => {
                        setCorrLocal(e.target.value);
                        commitCorr(e.target.value);
                    }}
                    sx={{ minWidth: 180 }}
                />

                <MoreFiltersPopover />
            </FilterRow>

            {/* ── Row 2: active-filter chip strip (only when non-default) ── */}
            {hasNonDefault && (
                <ChipStrip
                    gap={tokens.aliasesSpaceComponentDense}
                    mt={tokens.aliasesSpaceComponentDefault}
                >
                    <Typography variant="captionNeutral">Active:</Typography>

                    {filters.range !== '24h' && (
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={RANGE_LABEL[filters.range] || filters.range}
                            onDelete={() => setFilter('range', '24h')}
                        />
                    )}

                    {filters.actions.map((a) => (
                        <Chip
                            key={a}
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={ACTION_LABEL[a] || a}
                            onDelete={() =>
                                setFilter('actions', filters.actions.filter((x) => x !== a))
                            }
                        />
                    ))}

                    {filters.categories.map((c) => (
                        <Chip
                            key={c}
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={CATEGORY_LABEL[c] || c}
                            onDelete={() =>
                                setFilter('categories', filters.categories.filter((x) => x !== c))
                            }
                        />
                    ))}

                    {filters.providers.map((p) => (
                        <Chip
                            key={p}
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={PROVIDER_LABEL[p] || p}
                            onDelete={() =>
                                setFilter('providers', filters.providers.filter((x) => x !== p))
                            }
                        />
                    ))}

                    {filters.correlationId && (
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={`ID: ${filters.correlationId.slice(0, 8)}…`}
                            onDelete={() => setFilter('correlationId', '')}
                        />
                    )}

                    {filters.status && (
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={`Status: ${filters.status}`}
                            onDelete={() => setFilter('status', '')}
                        />
                    )}

                    {filters.tenantId && (
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={`Tenant: ${filters.tenantId}`}
                            onDelete={() => setFilter('tenantId', '')}
                        />
                    )}

                    {filters.userId && (
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={`User: ${filters.userId}`}
                            onDelete={() => setFilter('userId', '')}
                        />
                    )}

                    {filters.contentType && (
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={`Type: ${filters.contentType}`}
                            onDelete={() => setFilter('contentType', '')}
                        />
                    )}

                    {filters.apiKeyIds.map((id) => (
                        <Chip
                            key={id}
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            label={`Key: ${id}`}
                            onDelete={() =>
                                setFilter('apiKeyIds', filters.apiKeyIds.filter((x) => x !== id))
                            }
                        />
                    ))}

                    <Button
                        bdsType="interactiveSubtle"
                        onClick={clearAll}
                        sx={{ marginLeft: 'auto' }}
                    >
                        Clear all
                    </Button>
                </ChipStrip>
            )}
        </Box>
    );
}
